import cx from 'classnames';
import Numeral from 'numeral';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { roundScore } from '../../../../../../shared/common/utils';
import { retry } from '../../../../../../shared/common/retry';
import { formatPhaseDisplay, getPhaseProgress } from '../../../../../../shared/common/bossPhases';
import { plural } from '../../../../../../shared/frontend/libs/utils';
import { asset, scaledAssetUrl } from '../../../../../../shared/frontend/services/assetResolverService';
import { createActionGetRaceProgress } from '../../actions/getRaceProgress';
import { resolveRoutePath } from '../../bundles/routes';
import { getBossRankingsPathForParams } from '../../components/container/raidFloatingMenu';
import { Link } from '../../../../../../shared/frontend/components/link';
import { Spinner } from '../../../../../../shared/frontend/components/presentational/spinner';
import { ComplexTooltip, SimpleTooltip } from '../../../../../../shared/frontend/components/presentational/tooltip';
import { VideoEmbed } from '../../../../../../shared/frontend/components/video/videoEmbed';
import frontendLog from '../../libs/frontendLog';
import { TApplicationState, TRaceProgressInstance, TRaceProgressServerState } from '../../libs/store';
import { apiServiceAsync, TGetRaceProgressParams } from '../../services/apiService';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import moment from 'moment';
import { DarkModeIconTooltip } from '../../../../../../shared/frontend/components/darkModeIconTooltip';

type TBossKillStripOwnProps = {
  instanceId?: string;
  raidSlug: string;
  regionSlug: string;
  difficulty: string;
  className?: string;
  faction?: string;
  refresh?: boolean;
};

export type TRaidRaceProgressProps = TBossKillStripOwnProps &
  TRaceProgressInstance & {
    showKillVideo?: (videoParams: any) => void;
  };

const ICON_HEIGHT = 40;

const RaidBossStripTooltip = memo(function RaidBossStripTooltip(props: { kill: any; difficulty: string; raid: any }) {
  const bossKill = props.kill;

  const defeatedList: any[] = [];
  const attemptedList: any[] = [];

  const attempts = bossKill.attemptedBy?.attempts || [];

  bossKill.defeatedBy.guilds.forEach((guild: any, index: number) => {
    const bestGuildAttempt = attempts.find((a: any) => a.guild.id === guild.guild.id);
    defeatedList.push(
      <div key={`${guild.guild.id}_name`} className="slds-m-top--small">
        <span className={`text-bold rio-faction-color--${guild.guild.faction}`}>{guild.guild.name}</span>
        <div>
          {Numeral(index + 1).format('o')}
          {bestGuildAttempt && (
            <>
              {' '}
              •{' '}
              {bestGuildAttempt.attempt?.pulls === null ? (
                'Pull Count Hidden'
              ) : (
                <>
                  {bestGuildAttempt.attempt?.pulls} {plural('Pull', bestGuildAttempt?.attempt?.pulls)}
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  });

  let detailedAttempt;

  const MAX_ENTRIES_TO_SHOW = 3;

  if (defeatedList.length < MAX_ENTRIES_TO_SHOW) {
    for (const attempt of attempts) {
      const isDefeated = bossKill.defeatedBy.guilds.find((d: any) => d.guild.id === attempt.guild.id);
      if (isDefeated) {
        continue;
      }

      let percentDisplayed =
        attempt.attempt.overall_percent === null ? 'Best Pull Hidden' : `${attempt.attempt.overall_percent}%`;

      if (attempt.attempt.overall_percent !== null) {
        const phaseProgress = getPhaseProgress(
          props.difficulty,
          bossKill.bossSummary.slug,
          {
            overallPercent: attempt.attempt.overall_percent / 100,
            elapsedMs: attempt.attempt.duration_ms
          }
        );
        if (phaseProgress) {
          percentDisplayed = formatPhaseDisplay(phaseProgress, (bossPercent) => `${roundScore(bossPercent * 100, 2)}%`);
        }
      }

      attemptedList.push(
        <div key={attempt.guild.id} className={'slds-m-top--small'}>
          <div className={cx('text-bold', `rio-faction-color--${attempt.guild.faction}`)}>
            {attempt.guild.name}
            {attempt.guildPrivacy?.shareRaidUntil &&
              moment.utc().isAfter(moment.utc(attempt.guildPrivacy.shareRaidUntil)) && (
                <DarkModeIconTooltip
                  shareRaidUntil={attempt.guildPrivacy.shareRaidUntil}
                  className="slds-m-left--x-small"
                />
              )}
          </div>
          {attempt.attempt.overall_percent === null && attempt.attempt.pulls === null ? (
            <>Attempts Hidden</>
          ) : (
            <>
              {percentDisplayed}
              {' • '}
              {attempt.attempt.pulls === null ? (
                'Pull Amount Hidden'
              ) : (
                <>
                  {attempt.attempt.pulls} {plural('pull', attempt.attempt.pulls)}
                </>
              )}
            </>
          )}
        </div>
      );

      if (defeatedList.length + attemptedList.length >= MAX_ENTRIES_TO_SHOW) {
        break;
      }
    }
  }

  detailedAttempt = (
    <div>
      {defeatedList.length > 0 && (
        <>
          <div className="text-muted">Defeated By</div>
          {defeatedList}
        </>
      )}
      {attemptedList.length > 0 && (
        <>
          <div className={`text-muted ${defeatedList.length > 0 ? 'slds-m-top--medium' : ''}`}>In Progress By</div>
          {attemptedList}
        </>
      )}
      {defeatedList.length === 0 && attemptedList.length === 0 && <div className="text-muted">Undefeated</div>}
    </div>
  );

  return (
    <div>
      {t(`raids:${props.raid.slug}.bosses.${bossKill.bossSummary.slug}.name`)}

      {detailedAttempt}
    </div>
  );
});

const RaidProgressBossListing = memo(function RaidProgressBossListing(props: TRaidRaceProgressProps) {
  const route = getBossRankingsPathForParams(props);

  return (
    <div className="slds-grid">
      {props.bossKills.map((kill: any) => {
        let percentShown = null;
        if (
          kill.defeatedBy.totalCount === 0 &&
          kill.attemptedBy?.attempts?.length > 0 &&
          kill.attemptedBy.attempts[0].attempt.overall_percent !== null
        ) {
          percentShown = (
            <div
              className="text-white text-bold slds-text-heading--small slds-text-align--center rio-text-shadow--normal rwf-bossicon--fighting-text"
              style={{ position: 'absolute', bottom: 20, width: '100%', pointerEvents: 'none' }}
            >
              {kill.attemptedBy.attempts[0].attempt.overall_percent}%
            </div>
          );

          const phaseProgress = getPhaseProgress(
            props.difficulty,
            kill.bossSummary.slug,
            {
              overallPercent: kill.attemptedBy.attempts[0].attempt.overall_percent / 100,
              elapsedMs: kill.attemptedBy.attempts[0].attempt.duration_ms
            }
          );
          if (phaseProgress) {
            percentShown = (
              <div
                className="text-white text-bold slds-text-heading--small slds-text-align--center rio-text-shadow--normal rwf-bossicon--fighting-text"
                style={{ position: 'absolute', bottom: 20, width: '100%', pointerEvents: 'none' }}
              >
                {formatPhaseDisplay(phaseProgress, (bossPercent) => `${roundScore(bossPercent * 100, 0)}%`)}
              </div>
            );
          }
        }

        return (
          <div className="slds-col" key={kill.bossSummary.slug}>
            <div className="slds-grid slds-grid--vertical slds-grid--align-end">
              <div
                className="slds-col slds-text-align--center slds-m-bottom--x-small image-wrapper-height-full"
                style={{ height: ICON_HEIGHT }}
              >
                {kill.defeatedBy.totalCount > 0 && kill.defeatedBy.guilds[0]?.guild.logo && (
                  <Link
                    to={`${kill.defeatedBy.guilds[0]?.guild.path}/raid-encounters/${props.difficulty}/${props.raid.slug}/${kill.boss}`}
                  >
                    <img
                      src={scaledAssetUrl(kill.defeatedBy.guilds[0].guild.logo, {
                        width: 70
                      })}
                    />
                  </Link>
                )}
                {kill.defeatedBy.totalCount === 0 &&
                  kill.attemptedBy?.attempts?.length > 0 &&
                  kill.attemptedBy.attempts[0].guild.logo && (
                    <>
                      <img className="rwf-bossicon--fighting" src={kill.attemptedBy.attempts[0].guild.logo} />
                      {kill.attemptedBy.attempts[0].guildPrivacy?.shareRaidUntil &&
                        moment.utc().isAfter(moment.utc(kill.attemptedBy.attempts[0].guildPrivacy.shareRaidUntil)) && (
                          <DarkModeIconTooltip
                            shareRaidUntil={kill.attemptedBy.attempts[0].guildPrivacy.shareRaidUntil}
                            className="slds-m-left--small"
                          />
                        )}
                    </>
                  )}
              </div>
              <div className="slds-col slds-grow-none slds-text-align--center" style={{ position: 'relative' }}>
                <Link
                  to={resolveRoutePath(route, {
                    ...props,
                    raid: props.raid.slug,
                    difficulty: props.difficulty,
                    region: props.region.slug,
                    boss: kill.bossSummary.slug
                  })}
                  style={{ position: 'relative' }}
                >
                  <ComplexTooltip
                    tooltip={<RaidBossStripTooltip raid={props.raid} kill={kill} difficulty={props.difficulty} />}
                    className="nowrap"
                    renderJustChildren
                    preventClickOnTouchIfNotOpen
                  >
                    <img
                      src={asset(`/images/${props.raid.slug}/portraits/${kill.bossSummary.slug}.png`, {
                        width: 205,
                        quality: 75
                      })}
                      className={cx('rwf-bossicon', {
                        'rwf-bossicon--defeated': kill.defeatedBy.totalCount > 0,
                        'rwf-bossicon--fighting':
                          kill.defeatedBy.totalCount === 0 && kill.attemptedBy?.attempts?.length > 0
                      })}
                    />
                  </ComplexTooltip>
                </Link>
                {percentShown}
                {kill.defeatedBy.totalCount > 0 && kill.bossKillVideo && kill.defeatedBy.guilds[0] && (
                  <div
                    className="text-white text-bold slds-text-heading--small slds-text-align--center"
                    style={{ position: 'absolute', bottom: 0, width: '100%', cursor: 'pointer' }}
                    onClick={() => {
                      if (props.showKillVideo) {
                        props.showKillVideo({
                          video: kill.bossKillVideo,
                          boss: kill.bossSummary,
                          guild: kill.defeatedBy.guilds[0].guild,
                          attempt: kill.attemptedBy?.attempts?.length > 0 ? kill.attemptedBy.attempts[0].attempt : null
                        });
                      }
                    }}
                  >
                    <SimpleTooltip placement="top" tag="div" tooltip="Watch World First Video">
                      <img
                        src={asset('/images/rwf/video_icon.png')}
                        style={{ height: 24 }}
                        className="rio-shadow--large"
                      />
                    </SimpleTooltip>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

const REFRESH_TIMEOUT_SECONDS = 53;
const INITIAL_LOAD_RETRY_DELAY_MS = 1000;
const INITIAL_LOAD_MAX_RETRY_DELAY_MS = 60 * 1000;

function BossKillStrip(props: TBossKillStripOwnProps) {
  const { instanceId = 'default', raidSlug, regionSlug, difficulty, className, faction, refresh } = props;
  const [videoInfo, setVideoInfo] = useState<any>(null);
  const dispatch = useDispatch();

  const stateData = useSelector(
    (state: TApplicationState) => (state as any).raceProgress?.instances?.[instanceId] as TRaceProgressInstance
  );

  const fetchIdRef = useRef(0);
  const retryAbortRef = useRef<AbortController | null>(null);
  const currentLoadKey = `${instanceId}:${raidSlug}:${difficulty}:${regionSlug}:${faction || ''}`;
  const initialLoadKeyRef = useRef<string | null>(null);

  const fetchData = useCallback(
    async ({ retryOnError = false }: { retryOnError?: boolean } = {}) => {
      retryAbortRef.current?.abort();
      const retryAbortController = new AbortController();
      retryAbortRef.current = retryAbortController;

      const currentFetchId = ++fetchIdRef.current;
      const loadParams: TGetRaceProgressParams = {
        raid: raidSlug,
        difficulty,
        region: regionSlug,
        faction: faction || '',
        includeLogData: true
      };

      const getRaceProgress = async () => {
        const result = (await apiServiceAsync.getRaceProgress(loadParams)) as TRaceProgressServerState | undefined;

        if (!result) {
          throw new Error('BossKillStrip getRaceProgress got empty result');
        }

        return result;
      };

      try {
        const result = retryOnError
          ? await retry(getRaceProgress, {
              retries: Number.POSITIVE_INFINITY,
              initialDelay: INITIAL_LOAD_RETRY_DELAY_MS,
              delayFunction: (attemptNumber, initialDelay) =>
                Math.min(initialDelay * Math.pow(2, attemptNumber - 1), INITIAL_LOAD_MAX_RETRY_DELAY_MS),
              onRetry: (error, attemptNumber, delay) => {
                frontendLog.log({ err: error, attemptNumber, delay }, 'BossKillStrip getRaceProgress retrying');
              },
              signal: retryAbortController.signal,
              shouldRetry: async () => currentFetchId === fetchIdRef.current
            })
          : await getRaceProgress();

        if (currentFetchId !== fetchIdRef.current) {
          return;
        }

        dispatch(createActionGetRaceProgress(instanceId, result.raceProgress));
      } catch (err) {
        if (currentFetchId !== fetchIdRef.current || retryAbortController.signal.aborted) {
          return;
        }

        frontendLog.error({ err }, 'BossKillStrip getRaceProgress failed');
      }
    },
    [raidSlug, difficulty, regionSlug, faction, instanceId, dispatch]
  );

  useEffect(() => {
    if (initialLoadKeyRef.current === currentLoadKey) {
      return;
    }

    initialLoadKeyRef.current = currentLoadKey;
    void fetchData({ retryOnError: !stateData?.timeline });
  }, [currentLoadKey, stateData?.timeline, fetchData]);

  useEffect(() => {
    return () => {
      fetchIdRef.current += 1;
      retryAbortRef.current?.abort();
      initialLoadKeyRef.current = null;
    };
  }, []);

  // Tab visibility-aware auto-refresh: when the user switches back to this tab,
  // immediately refresh data so they see the latest race progress without waiting
  // for the next interval tick.
  useEffect(() => {
    if (refresh === false) {
      return;
    }

    if (!stateData?.timeline) {
      return;
    }

    const doRefresh = () => {
      if (document.hidden) {
        return;
      }
      void fetchData();
    };

    const interval = setInterval(doRefresh, REFRESH_TIMEOUT_SECONDS * 1000);

    const onVisibilityChange = () => {
      if (!document.hidden) {
        void fetchData();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refresh, fetchData, stateData?.timeline]);

  const showKillVideo = useCallback((videoParams: any) => {
    setVideoInfo(videoParams);
  }, []);

  const hideVideo = useCallback((ev: any) => {
    ev.preventDefault();
    setVideoInfo(null);
  }, []);

  if (!stateData?.raid || !stateData?.bossKills) {
    return (
      <div className={className} style={{ position: 'relative', height: 80 }}>
        <Spinner />
      </div>
    );
  }

  const combinedProps: TRaidRaceProgressProps = {
    ...props,
    ...stateData,
    showKillVideo
  };

  return (
    <div className={className}>
      {videoInfo && videoInfo.video.length > 0 && (
        <div>
          <div style={{ background: '#151314', height: 480 }} className="slds-p-around--large slds-m-bottom--large">
            <div className="text-bold slds-text-align--center" style={{ position: 'relative' }}>
              <div className="slds-text-heading--large rio-text--shadow">
                {t('rankings:world-first-boss', { bossName: videoInfo.boss.name })}
              </div>
              <div className="slds-text-heading--medium rio-text--shadow">
                {videoInfo.attempt ? (
                  <Trans
                    i18nKey="rankings:defeated-by-guild-with-pulls"
                    values={{ guildName: videoInfo.guild.name, count: videoInfo.attempt.pulls }}
                    components={[<span className="text-bold" />]}
                  />
                ) : (
                  <Trans
                    i18nKey="rankings:defeated-by-guild"
                    values={{ guildName: videoInfo.guild.name }}
                    components={[<span className="text-bold" />]}
                  />
                )}
              </div>
              <span
                className="slds-badge rio-badge rio-badge-color--light rio-badge-size--medium
                rio-border--light rio-shadow--small rio-text-shadow--normal"
                style={{ position: 'absolute', right: '1rem', top: '0.5rem' }}
              >
                <button className="slds-button" onClick={hideVideo}>
                  {t('rankings:close')}
                </button>
              </span>
            </div>
            <div className="slds-p-around--large">
              <div className="slds-align--absolute-center" style={{ maxWidth: 533, maxHeight: 300 }}>
                <VideoEmbed type={videoInfo.video[0].type} id={videoInfo.video[0].id} time={videoInfo.video[0].videoTimestampSeconds} />
              </div>
            </div>
          </div>
        </div>
      )}
      <RaidProgressBossListing {...combinedProps} />
    </div>
  );
}

export default memo(BossKillStrip);
